import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '../../context/TourContext';
import Shepherd from 'shepherd.js';
import { getHomeSteps, getStrategistSteps } from './tourSteps';

type Tour = InstanceType<typeof Shepherd.Tour>;

/** Wait for a DOM element matching `selector` to appear, with timeout. */
function waitForElement(selector: string, timeoutMs = 5000): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) {
      resolve(el);
      return;
    }

    const start = Date.now();
    const observer = new MutationObserver(() => {
      const found = document.querySelector<HTMLElement>(selector);
      if (found) {
        observer.disconnect();
        resolve(found);
      } else if (Date.now() - start > timeoutMs) {
        observer.disconnect();
        resolve(null);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Safety timeout
    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
  });
}

/**
 * TourController — bridges TourContext state with Shepherd.js Tour instances.
 * Renders nothing; manages the tour lifecycle via effects.
 */
const TourController: React.FC = () => {
  const { shouldStartTour, currentSegment, completeTour, dismissTour, advanceToSegment } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const tourRef = useRef<Tour | null>(null);
  const hasStartedHome = useRef(false);
  const hasStartedStrategist = useRef(false);

  /** Cleanup any active tour instance */
  const destroyTour = useCallback(() => {
    if (tourRef.current) {
      // Remove listeners before destroying to avoid double-firing
      tourRef.current.off('cancel');
      tourRef.current.off('complete');
      if (tourRef.current.isActive()) {
        tourRef.current.hide();
      }
      tourRef.current = null;
    }
  }, []);

  /** Create a new Shepherd Tour with common options */
  const createTour = useCallback(() => {
    return new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
      },
      exitOnEsc: true,
      keyboardNavigation: true,
    });
  }, []);

  // ── Home segment ──
  useEffect(() => {
    if (!shouldStartTour || currentSegment !== 'home') return;
    if (location.pathname !== '/home') return;
    if (hasStartedHome.current) return;

    let cancelled = false;

    const startHomeTour = async () => {
      await waitForElement('[data-tour="hero-grid"]');
      if (cancelled) return;

      hasStartedHome.current = true;
      destroyTour();

      const tour = createTour();
      tourRef.current = tour;

      const steps = getHomeSteps(() => {
        // Called when user clicks "Open Strategist"
        destroyTour();
        advanceToSegment('strategist');
        navigate('/strategist');
      });

      tour.addSteps(steps);

      tour.on('cancel', () => {
        destroyTour();
        dismissTour();
      });

      await tour.start();
    };

    // Small delay to let the page fully render
    const timer = setTimeout(startHomeTour, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    shouldStartTour,
    currentSegment,
    location.pathname,
    navigate,
    destroyTour,
    createTour,
    advanceToSegment,
    dismissTour,
  ]);

  // ── Strategist segment ──
  useEffect(() => {
    if (!shouldStartTour || currentSegment !== 'strategist') return;
    if (location.pathname !== '/strategist') return;
    if (hasStartedStrategist.current) return;

    let cancelled = false;

    const startStrategistTour = async () => {
      await waitForElement('[data-tour="strategist-header"]');
      if (cancelled) return;

      hasStartedStrategist.current = true;
      destroyTour();

      const tour = createTour();
      tourRef.current = tour;

      const steps = getStrategistSteps(() => {
        // Called on "Finish Tour"
        completeTour();
      });

      tour.addSteps(steps);

      tour.on('cancel', () => {
        destroyTour();
        dismissTour();
      });

      tour.on('complete', () => {
        completeTour();
      });

      await tour.start();
    };

    const timer = setTimeout(startStrategistTour, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shouldStartTour, currentSegment, location.pathname, destroyTour, createTour, completeTour, dismissTour]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      destroyTour();
    };
  }, [destroyTour]);

  // Reset segment tracking when tour state resets
  useEffect(() => {
    if (!shouldStartTour) {
      hasStartedHome.current = false;
      hasStartedStrategist.current = false;
    }
  }, [shouldStartTour]);

  return null;
};

export default TourController;
